// New-match draft — DESIGN.md section 9.1 screen 2. Lives outside NewMatch.vue so a badge scan
// on Home (which fills the left side and jumps straight to New match) and the scanner picking
// up a badge while already on New match both write to the same place.
import { reactive, watch } from 'vue'
import { getHeadToHead } from '../backend'
import { MATCH_TYPE_SERVE_EVERY_DEFAULTS } from '../types'
import type { MatchTypeCode, Player, Side } from '../types'

// Round-12 (2026-08-31): the LAST-USED match format (type + best-of + serve-every) is the
// default for every next game — persisted to localStorage so it also survives reloads. A
// tournament rule dictating a format will override this later (not implemented yet); the
// tournament PREFILL path already leaves these fields untouched, so whatever was last used
// still applies there today.
const FORMAT_KEY = 'pingpong.lastMatchFormat.v1'

interface PersistedFormat {
  matchTypeCode: MatchTypeCode
  bestOf: number
  serveEvery: number
}

function loadLastFormat(): PersistedFormat | null {
  try {
    const raw = localStorage.getItem(FORMAT_KEY)
    if (!raw) return null
    const p = JSON.parse(raw)
    if ((p.matchTypeCode === 'rapid11' || p.matchTypeCode === 'classic21') && typeof p.bestOf === 'number' && typeof p.serveEvery === 'number') {
      return p as PersistedFormat
    }
  } catch { /* corrupt storage — fall through to defaults */ }
  return null
}

interface Draft {
  left: Player | null
  right: Player | null
  activeSide: Side
  matchTypeCode: MatchTypeCode
  bestOf: number
  firstServer: Side
  // 2026-08-31 user feedback: per-match serve-rotation interval, options in
  // types.ts's SERVE_EVERY_OPTIONS (2/3/5). Starts at the initial match type's own default
  // (MATCH_TYPE_SERVE_EVERY_DEFAULTS) — NewMatch.vue's pickMatchType keeps it in sync whenever
  // the mode toggles, per round-3 feedback item 2.
  serveEvery: number
  // 2026-08-31 round-8 feedback item 21 — set by prefillFromTournamentSlot when New match was
  // reached via a Tournament bracket slot's "start" action; NewMatch.vue's start() forwards it
  // to createMatch so the real backend can link the match back to that slot. Cleared by
  // resetDraft() like everything else once the match is actually created.
  tournamentSlotId: number | null
}

function initial(): Draft {
  const last = loadLastFormat()
  return {
    left: null,
    right: null,
    activeSide: 'left',
    // Round-12: default to the LAST-USED format/best-of/serve-every (localStorage), falling
    // back to rapid11 / Bo3 (2026-08-31 user feedback item 1) on first run.
    matchTypeCode: last?.matchTypeCode ?? 'rapid11',
    bestOf: last?.bestOf ?? 3,
    firstServer: 'left',
    serveEvery: last?.serveEvery ?? MATCH_TYPE_SERVE_EVERY_DEFAULTS.rapid11,
    tournamentSlotId: null,
  }
}

export const draft = reactive<Draft>(initial())

// Persist every format change immediately (not on match start): "keep the last match format for
// any games" should survive even a draft that's abandoned before Start.
watch(
  () => [draft.matchTypeCode, draft.bestOf, draft.serveEvery] as const,
  ([matchTypeCode, bestOf, serveEvery]) => {
    try {
      localStorage.setItem(FORMAT_KEY, JSON.stringify({ matchTypeCode, bestOf, serveEvery }))
    } catch { /* storage unavailable — defaults just won't persist */ }
  },
)

// ---------------------------------------------------------------------------------------------
// First-server suggestion (round-3 feedback item 3) — "whoever has served first less" (server-
// side count, GET /api/rankings/head-to-head, extended with suggestedFirstServerPlayerId — see
// DESIGN.md 7.7). Recomputed any time the picked PAIR changes; cached per pair so a swap (same
// two ids, different sides) re-applies instantly with no network round trip. A manual tap on the
// Serves-first buttons (NewMatch.vue) is never overwritten again until the pair itself changes.
// ---------------------------------------------------------------------------------------------
let firstServerRequestId = 0
let suggestionPairKey: string | null = null
let suggestedPlayerId: number | null = null

function pairKey(aId: number, bId: number): string {
  return aId < bId ? `${aId}:${bId}` : `${bId}:${aId}`
}

function applySuggestion() {
  if (suggestedPlayerId == null) return
  if (draft.left?.id === suggestedPlayerId) draft.firstServer = 'left'
  else if (draft.right?.id === suggestedPlayerId) draft.firstServer = 'right'
}

async function refreshFirstServerSuggestion() {
  const { left, right } = draft
  if (!left || !right || left.id === right.id) return
  const key = pairKey(left.id, right.id)
  if (key === suggestionPairKey && suggestedPlayerId != null) {
    applySuggestion() // same pair as last time (e.g. a swap) — no need to re-fetch
    return
  }
  const requestId = ++firstServerRequestId
  try {
    const hh = await getHeadToHead(left.id, right.id)
    if (requestId !== firstServerRequestId) return // stale — the pair changed again meanwhile
    if (draft.left?.id !== left.id || draft.right?.id !== right.id) return // pair changed
    suggestionPairKey = key
    suggestedPlayerId = hh.suggestedFirstServerPlayerId
    applySuggestion()
  } catch {
    // Best-effort preselect — a failed lookup just leaves firstServer at whatever it already was.
  }
}

export function resetDraft() {
  Object.assign(draft, initial())
  suggestionPairKey = null
  suggestedPlayerId = null
}

/** Fill the currently active side; if both sides end up with the same player, the caller
 * (NewMatch.vue) is responsible for showing the inline rejection — we still store it so the UI
 * can point at exactly what's wrong. */
export function fillActiveSide(player: Player) {
  draft[draft.activeSide] = player
  draft.activeSide = draft.activeSide === 'left' ? 'right' : 'left'
  void refreshFirstServerSuggestion()
}

export function setSide(side: Side, player: Player | null) {
  draft[side] = player
  void refreshFirstServerSuggestion()
}

export function swapDraftSides() {
  const tmp = draft.left
  draft.left = draft.right
  draft.right = tmp
  void refreshFirstServerSuggestion()
}

/** Round-8 feedback item 21 — Tournaments' bracket Explorer "start" action: resets the draft
 * (a bracket slot's players fully replace whatever was picked before, unlike fillActiveSide's
 * incremental fill) and prefills both sides plus `tournamentSlotId` so NewMatch.vue's start()
 * carries the link through to createMatch. The caller still navigates to 'newMatch' itself —
 * this only touches the store, matching draft.ts's existing division of labor. */
export function prefillFromTournamentSlot(left: Player, right: Player, tournamentSlotId: number) {
  Object.assign(draft, initial())
  draft.left = left
  draft.right = right
  draft.tournamentSlotId = tournamentSlotId
  void refreshFirstServerSuggestion()
}
